import React from "react";
import { MdNotifications, MdClose, MdSend } from "react-icons/md";
import { notificationService } from "services/notificationService";
import { useNotification } from "context/NotificationContext";
import { useLanguage } from "context/LanguageContext";

interface Props {
  sessionId: string;
  sessionTitle?: string;
  onClose: () => void;
}

const NotifySessionModal: React.FC<Props> = ({
  sessionId,
  sessionTitle,
  onClose,
}) => {
  const { addNotification } = useNotification();
  const { t, locale } = useLanguage();
  const [title, setTitle] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [targetRole, setTargetRole] = React.useState("");
  const [scheduledAt, setScheduledAt] = React.useState("");
  const [sending, setSending] = React.useState(false);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const handleSend = async () => {
    setSending(true);
    try {
      const result = await notificationService.notifySession(
        sessionId,
        title.trim() || undefined,
        message.trim() || undefined,
        targetRole || undefined,
        scheduledAt ? new Date(scheduledAt).toISOString() : undefined
      );
      addNotification(
        result.scheduled
          ? t("notifications.scheduledSuccess", { count: result.sent_count })
          : t("notifySession.sentSuccess", { count: result.sent_count }),
        "success"
      );
      onClose();
    } catch (err) {
      addNotification(
        err instanceof Error ? err.message : t("notify.sendFailed"),
        "error"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-2xl border border-line bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div className="flex items-center gap-2">
            <MdNotifications className="h-5 w-5 text-brand-500" />
            <div>
              <p className="text-sm font-semibold text-ink">
                {t("notify.modalTitle")}
              </p>
              {sessionTitle && (
                <p className="text-xs text-ink-muted">
                  {sessionTitle}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            aria-label={t("common.close")}
            onClick={onClose}
            className="rounded-lg p-1 text-ink-subtle hover:bg-surface-hover hover:text-ink"
          >
            <MdClose className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted">
              {t("notifySession.sendTo")}
            </label>
            <select
              value={targetRole}
              onChange={(e) => setTargetRole(e.target.value)}
              className="w-full rounded-lg border border-line bg-navy-50 px-3 py-2 text-sm text-ink focus:border-brand-500 focus:outline-none"
            >
              <option value="">{t("notifications.roleFilter.ALL")}</option>
              <option value="student">{t("enum.role.STUDENT")}</option>
              <option value="staff">{t("notifySession.staffOption")}</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted">
              {t("notify.titleLabel")}{" "}
              <span className="text-ink-subtle">{t("notify.titleOptional")}</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("notify.titlePlaceholder")}
              className="w-full rounded-lg border border-line bg-navy-50 px-3 py-2 text-sm text-ink placeholder-gray-400 focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted">
              {t("notify.messageLabel")}{" "}
              <span className="text-ink-subtle">{t("notify.titleOptional")}</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder={t("notify.messagePlaceholder")}
              className="w-full resize-none rounded-lg border border-line bg-navy-50 px-3 py-2 text-sm text-ink placeholder-gray-400 focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted">
              {t("notifications.scheduleLabel")}{" "}
              <span className="text-ink-subtle">
                {t("notifications.optional")}
              </span>
            </label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="w-full rounded-lg border border-line bg-navy-50 px-3 py-2 text-sm text-ink focus:border-brand-500 focus:outline-none"
            />
            {scheduledAt && (
              <p className="mt-1 text-xs text-brand-500">
                {t("notifications.scheduledAt", {
                  time: new Date(scheduledAt).toLocaleString(locale),
                })}
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-line px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-gray-600 hover:bg-surface-hover"
          >
            {t("action.cancel")}
          </button>
          <button
            type="button"
            disabled={sending}
            onClick={handleSend}
            className="flex items-center gap-1.5 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            <MdSend className="h-4 w-4" />
            {sending
              ? t("notify.sendingBtn")
              : scheduledAt
              ? t("notifySession.scheduleSend")
              : t("notify.sendBtn")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotifySessionModal;
